import React, { useState } from "react";
import {
  Box,
  Paper,
  IconButton,
  Collapse,
  Typography,
  Button,
  alpha,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckIcon from "@mui/icons-material/Check";
import { FieldControl } from "../../../shared/components";
import { FilterKey, Filters, useFilterContext, FilterItem } from "../../../shared/context/FilterContext";
import { AsyncMultiAutoComplete } from "../../../shared/components/AsyncMultiAutoComplete";
import { useInfiniteSites } from "../../projects/hooks/useSites";
import { useUserFilterActions } from "../../../hooks/useUserFilterActions";

interface MapFiltersProps {
  filters: Filters;
  tempFilters: Filters;
  updateFilter: (key: FilterKey, value: FilterItem[]) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  projectCount: number;
}

export const MapFilters: React.FC<MapFiltersProps> = ({
  filters,
  tempFilters,
  updateFilter,
  applyFilters,
  resetFilters,
  hasActiveFilters,
  projectCount,
}) => {

  const { options } = useFilterContext();
  const { getFilteredData } = useUserFilterActions();

  const [siteSearch, setSiteSearch] = useState("");

  const normalizedRegion = tempFilters.region?.length
    ? tempFilters.region.map(r => Number(r.value))
    : null;

  const {
          sites,
          fetchNextPage,
          hasNextPage,
          isFetchingNextPage,
          isLoading: isSitesLoading,
  } = useInfiniteSites(siteSearch, normalizedRegion, null);

  const [isExpanded, setIsExpanded] = React.useState(false);

  const hasPendingChanges =
    JSON.stringify(tempFilters) !== JSON.stringify(filters);

  const handleApplyAndClose = () => {
    applyFilters();
  };

  const { regions, workstreams } = getFilteredData({
    regionIds: tempFilters.region?.length ? tempFilters.region.map(r => Number(r.value)) : null,
  });

  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: 16,
        right: 16,
        zIndex: 20,
        borderRadius: 2,
        overflow: "hidden",
        minWidth: isExpanded ? 320 : "auto",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha(theme.palette.background.paper, 0.98),
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          borderBottom: isExpanded ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              bgcolor: hasActiveFilters ? "primary.main" : "action.hover",
              color: hasActiveFilters ? "primary.contrastText" : "inherit",
              "&:hover": {
                bgcolor: hasActiveFilters ? "primary.dark" : "action.selected",
              },
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
          {!isExpanded && (
            <Typography variant="body2" color="text.secondary">
              {projectCount} projects
            </Typography>
          )}
        </Box>

        {isExpanded && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle2">Filters</Typography>
            <IconButton size="small" onClick={() => setIsExpanded(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <FieldControl
            keyName={"programme"}
            label={"Programme"}
            variant={"select"}
            options={(options.programme || [])
              .filter((r: any) => r?.description)
              .sort((a: any, b: any) =>
                (a.description ?? "").localeCompare(b.description ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.description,
              }))}
            value={tempFilters.programme.map(p => p.value)}
            onChange={(_key: string, value: string[]) => {
              const items = options.programme
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({
                  value: String(r.id),
                  label: r.description,
                }));
              updateFilter("programme", items);
            }}
            multiSelect
          />

          <FieldControl
            keyName={"region"}
            label={"Network"}
            variant={"select"}
            options={[...regions]
              .sort((a: any, b: any) =>
                (a.name ?? "").localeCompare(b.name ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.name,
              }))}
            value={tempFilters.region.map(r => r.value)}
            onChange={(_key: string, value: string[]) => {
              const items = regions
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({ value: String(r.id), label: r.name }));
              updateFilter("region", items);
            }}
            multiSelect
          />

          <FieldControl
            keyName={"workstream"}
            label={"Workstream"}
            variant={"select"}
            options={[...workstreams]
              .sort((a: any, b: any) =>
                (a.name ?? "").localeCompare(b.name ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.name,
              }))}
            value={tempFilters.workstream.map(w => w.value)}
            onChange={(_key: string, value: string[]) => {
              const items = workstreams
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({ value: String(r.id), label: r.name }));
              updateFilter("workstream", items);
            }}
            multiSelect
          />

          <FieldControl
            keyName={"worktype-group"}
            label={"Worktype Group"}
            variant={"select"}
            options={(options["worktype-group"] || [])
              .filter((r: any) => r?.description)
              .sort((a: any, b: any) =>
                (a.description ?? "").localeCompare(b.description ?? ""),
              )
              .map((r: any) => ({
                value: String(r.id),
                label: r.description,
              }))}
            value={tempFilters["worktype-group"].map(wt => wt.value)}
            onChange={(_key: string, value: string[]) => {
              const items = options["worktype-group"]
                .filter((r: any) => value.includes(String(r.id)))
                .map((r: any) => ({
                  value: String(r.id),
                  label: r.description,
                }));
              updateFilter("worktype-group", items);
            }}
            multiSelect
          />
          
          <AsyncMultiAutoComplete
              label="Site"
              value={tempFilters.site ?? []}
              onChange={(value) => updateFilter('site', value as FilterItem[])}
              options={sites.map((s: any) => ({
                  label: s.description,
                  value: String(s.id),
              }))}
              onSearch={(text) => setSiteSearch(text)}
              onScrollEnd={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                      fetchNextPage();
                  }
              }}
              loading={isSitesLoading || isFetchingNextPage}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {projectCount} projects found
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={resetFilters}
                disabled={!hasActiveFilters && !hasPendingChanges}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={handleApplyAndClose}
                disabled={!hasPendingChanges}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};
